<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';
import { parseAdNote } from './adNote.js';

const { contentAttributes, content, attachments } = useMessageContext();
const { t } = useI18n();

// Ghi chú cũ chỉ còn markdown; parser tách riêng để có chỗ test.
const fromContent = computed(() => parseAdNote(content.value));

const ad = computed(() => {
  const attrs = contentAttributes.value?.bonbozAd;
  return attrs?.adId ? attrs : fromContent.value;
});

// Chuoi ung vien theo do ben: attachment truoc — voi ad moi, backend da TAI
// creative ve ActiveStorage nen `dataUrl` la URL local vinh vien; `imageUrl`
// trong `bonboz_ad` luon la fbcdn ky + co han (`oe=`), vai thang la 403 du
// quang cao van chay. Voi ghi chu cu attachment chi co `external_url` (chinh
// cai fbcdn do) — Set de khoi thu cung mot URL hai lan.
const candidates = computed(() => [
  ...new Set(
    [
      attachments.value?.find(item => item.fileType === 'image')?.dataUrl,
      ad.value.imageUrl,
    ].filter(Boolean)
  ),
]);

// Anh hong thi lui sang ung vien ke tiep, het moi bao "khong tai duoc" —
// truoc day mot lan loi la chet luon va khong bao gio reset, ke ca khi src
// doi (component tai dung lai giua cac tin nhan).
const viTri = ref(0);
watch(candidates, () => {
  viTri.value = 0;
});
const imageUrl = computed(() => candidates.value[viTri.value]);
const anhHong = computed(() => candidates.value.length > 0 && !imageUrl.value);

// Cat bang JS chu khong line-clamp: "Xem them" phai nam ngay sau dau ba cham.
const BODY_LIMIT = 120;
const expanded = ref(false);
const body = computed(() => ad.value.body?.trim() ?? '');
const isLong = computed(() => body.value.length > BODY_LIMIT);
const shownBody = computed(() =>
  expanded.value || !isLong.value
    ? body.value
    : `${body.value.slice(0, BODY_LIMIT).trimEnd()}…`
);
</script>

<template>
  <BaseBubble
    class="p-3 w-[min(330px,calc(100cqw-5rem))]"
    data-bubble-name="ad-card"
  >
    <div class="flex items-center gap-1.5">
      <Icon icon="i-ri-meta-fill" class="size-4 shrink-0" />
      <span class="text-xs truncate">
        {{ t('BONBOZ.TIN_NHAN.AD_ID', { id: ad.adId }) }}
      </span>
      <a
        v-if="ad.permalinkUrl"
        :href="ad.permalinkUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="grid ml-auto rounded-md size-6 shrink-0 place-items-center bg-n-alpha-black1"
      >
        <Icon icon="i-lucide-external-link" class="size-3.5" />
      </a>
    </div>
    <template v-if="candidates.length">
      <!-- DA THU `referrerpolicy` = "no-referrer" VA DA GO RA (27/08/2026).
           Gia thuyet luc do: fbcdn tu choi yeu cau mang Referer tu ten mien la.
           Do tren du lieu that thi NGUOC LAI — bo Referer di la MOI anh quang
           cao chet, ke ca nhung anh truoc do van tai binh thuong. Dung them lai. -->
      <img
        v-if="!anhHong"
        :key="imageUrl"
        :src="imageUrl"
        alt=""
        loading="lazy"
        decoding="async"
        class="object-cover w-full h-[176px] mt-2 rounded-lg"
        @error="viTri += 1"
      />
      <div
        v-else
        class="flex flex-col gap-1 justify-center items-center px-3 mt-2 text-xs text-center rounded-lg h-[88px] text-n-slate-11 bg-n-alpha-black1"
      >
        <Icon icon="i-lucide-image-off" class="size-4" />
        {{ t('BONBOZ.TIN_NHAN.AD_ANH_HET_HAN') }}
      </div>
    </template>
    <p v-if="body" class="mt-2 whitespace-pre-line">
      {{ shownBody
      }}<button
        v-if="isLong"
        class="ml-1 align-baseline text-n-blue-11"
        @click="expanded = !expanded"
      >
        {{
          expanded
            ? t('BONBOZ.TIN_NHAN.THU_GON')
            : t('BONBOZ.TIN_NHAN.XEM_THEM')
        }}
      </button>
    </p>
  </BaseBubble>
</template>
